/**
 * NsectionB4Page
 * B.4.規範性引用（A6.4-FORM-METH-002 Section 4. Normative references）
 * 三頁籤 Card + DataTable + Modal
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB4Service, { NsectionB4Detail, NsectionB4Update, ReferenceItem } from '../services/nsectionB4Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

type TabKey = 1 | 2 | 3;
type FieldKey = 'based_methodologies' | 'methodological_tools' | 'information_sources';

const TAB_FIELD_MAP: Record<number, FieldKey> = {
  1: 'based_methodologies',
  2: 'methodological_tools',
  3: 'information_sources',
};

const NsectionB4Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB4Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>(1);

  const [basedMethodologies, setBasedMethodologies] = useState<ReferenceItem[]>([]);
  const [methodologicalTools, setMethodologicalTools] = useState<ReferenceItem[]>([]);
  const [informationSources, setInformationSources] = useState<ReferenceItem[]>([]);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalReference, setModalReference] = useState('');

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NsectionB4Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    if (!selectedProposalId) {
      setData(null);
      setBasedMethodologies([]); setMethodologicalTools([]); setInformationSources([]);
      return;
    }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const result = await nsectionB4Service.getByProposal(selectedProposalId);
        if (result) {
          setData(result);
          setBasedMethodologies(result.based_methodologies || []);
          setMethodologicalTools(result.methodological_tools || []);
          setInformationSources(result.information_sources || []);
        } else {
          setData(null);
          setBasedMethodologies([]); setMethodologicalTools([]); setInformationSources([]);
        }
      } catch (err: any) {
        if (err.response?.status !== 404)
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
      } finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, isZh]);

  const getCurrentItems = (): ReferenceItem[] => {
    if (activeTab === 1) return basedMethodologies;
    if (activeTab === 2) return methodologicalTools;
    return informationSources;
  };

  const setCurrentItems = (items: ReferenceItem[]) => {
    if (activeTab === 1) setBasedMethodologies(items);
    else if (activeTab === 2) setMethodologicalTools(items);
    else setInformationSources(items);
  };

  const openModal = (index?: number) => {
    const items = getCurrentItems();
    if (index !== undefined) {
      setEditingIndex(index);
      setModalReference(items[index].reference);
    } else {
      setEditingIndex(null);
      setModalReference('');
    }
    setShowModal(true);
  };

  const handleModalConfirm = () => {
    const items = [...getCurrentItems()];
    const item: ReferenceItem = { reference: modalReference };
    if (editingIndex !== null) {
      items[editingIndex] = item;
    } else {
      items.push(item);
    }
    setCurrentItems(items);
    setShowModal(false);
  };

  const handleDelete = async (index: number) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: isZh ? '確定要刪除嗎？' : 'Are you sure?',
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    setCurrentItems(getCurrentItems().filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const fieldKey = TAB_FIELD_MAP[activeTab];
      const updateData: NsectionB4Update = {
        [fieldKey]: getCurrentItems(),
      };
      const result = await nsectionB4Service.update(selectedProposalId, updateData);
      setData(result);
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally { setLoading(false); }
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b4', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canUpdate = hasPermission('nsection_b4', 'update');

  const tabs = [
    { key: 1 as TabKey, zh: 'B.4.1.基礎方法論', en: 'B.4.1. Based methodologies',
      hint: isZh ? '本基線與監測方法論基於以下提案的新方法論及/或已核准或整合方法論：' : 'This proposed baseline and monitoring methodology is based on the following proposed new methodologies and/or approved or consolidated methodologies:' },
    { key: 2 as TabKey, zh: 'B.4.2.方法學工具', en: 'B.4.2. Methodological tools',
      hint: isZh ? '本方法論亦參照以下方法學工具的最新核准版本：' : 'This methodology also refers to the latest approved versions of the following methodological tools:' },
    { key: 3 as TabKey, zh: 'B.4.3.資料來源', en: 'B.4.3. Sources of information',
      hint: isZh ? '本方法論基於以下資料來源：' : 'This methodology is based on the following sources of information:' },
  ];

  const currentTab = tabs.find(t => t.key === activeTab)!;
  const currentItems = getCurrentItems();

  const cardStyle: React.CSSProperties = {
    width: '100%', background: '#fff', borderRadius: '10px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px',
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="nsection_b4" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div>
          {/* 頁籤 */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '20px' }}>
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                padding: '10px 24px', border: 'none', cursor: 'pointer', fontSize: '14px', marginBottom: '-2px',
                borderBottom: activeTab === tab.key ? '3px solid #4A90E2' : '3px solid transparent',
                background: activeTab === tab.key ? '#f0f7ff' : 'transparent',
                color: activeTab === tab.key ? '#4A90E2' : '#666', fontWeight: activeTab === tab.key ? 600 : 400,
              }}>
                {isZh ? tab.zh : tab.en}
              </button>
            ))}
          </div>

          {/* Card */}
          <div style={cardStyle}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginBottom: '6px' }}>
              {isZh ? currentTab.zh : currentTab.en}
            </div>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '20px', padding: '10px 14px', background: '#f9f9f9', borderRadius: '6px' }}>
              {currentTab.hint}
            </div>

            {canUpdate && (
              <div style={{ marginBottom: '12px', textAlign: 'right' }}>
                <button className="btn-primary" onClick={() => openModal()} style={{ fontSize: '13px', padding: '6px 16px' }}>
                  {t('common.create')}
                </button>
              </div>
            )}

            {currentItems.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                {isZh ? '尚無資料，請點擊「新增」按鈕' : 'No data yet. Click "Create" to add one.'}
              </div>
            ) : (
              <div className="data-table-container">
                <table className="data-table">
                  <thead className="table-header-dark-green">
                    <tr>
                      <th style={{ width: '5%' }}>#</th>
                      <th style={{ width: '80%' }}>{isZh ? '參照內容' : 'Reference'}</th>
                      <th style={{ width: '15%' }}>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td dangerouslySetInnerHTML={{ __html: item.reference || '-' }} />
                        <td className="actions">
                          {canUpdate && (
                            <>
                              <button className="btn-edit" onClick={() => openModal(idx)} style={{ marginRight: '8px' }}>{t('common.edit')}</button>
                              <button className="btn-delete" onClick={() => handleDelete(idx)}>{t('common.delete')}</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {canUpdate && (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '20px', borderTop: '1px solid #f0f0f0', marginTop: '16px' }}>
                <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                  {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>{editingIndex !== null ? (isZh ? '編輯參照' : 'Edit Reference') : (isZh ? '新增參照' : 'Add Reference')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>
                  {isZh ? '參照內容' : 'Reference'} *
                </label>
                <RichTextEditor value={modalReference} onChange={setModalReference}
                  placeholder={isZh ? '請輸入方法論/工具/資料來源參照...' : 'Enter reference...'} minHeight="150px" />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button className="btn-primary" onClick={handleModalConfirm}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NsectionB4Page;
